// @flow
import * as React from 'react';
import SortableTh from "../sorting/sortable-th";
import { format } from "../utils/format";
import Invoice from "./invoice.model";
import Organization from "../organization/organization.model";

type TableProps = {|
  list: List,
  URL: $ElementType<ReduxState, 'URL'>,
  invoices: $ElementType<ReduxState, 'invoices'>,
  events: $ElementType<ReduxState, 'events'>,
  organizations: $ElementType<ReduxState, 'organizations'>,
  accessgroups: $ElementType<ReduxState, 'accessgroups'>,
  showOrgColumn: boolean,
  calendarView: boolean,
  onInvoiceClick: Function
|};

export default function Table({
  URL,
  list,
  invoices,
  transactions,
  events,
  organizations,
  showOrgColumn,
  calendarView,
  progress,
  onInvoiceClick,
  onRefundClick,
  onEditClick,
  onIncreaseClick,
  deleteTransaction,
  setSorting,
  sorting
}: TableProps) {
  return (
    <div className="panel panel-default">
      <div className="table-responsive">
        <table className="table table-sm table-hover table-panel table-invoces">
          <thead>
          <tr>
            <SortableTh
              field="date"
              name="invoicesSorting"
              label="Date"
              sorting={sorting}
              innerClassName="text-left display-flex"
              onClick={setSorting}
            />
            <SortableTh
              field="type"
              name="invoicesSorting"
              label="Type"
              sorting={sorting}
              innerClassName="text-left display-flex"
              onClick={setSorting}
            />
            {showOrgColumn ? (
              <SortableTh
                field="organization"
                name="invoicesSorting"
                label="Organization"
                sorting={sorting}
                innerClassName="text-center display-flex"
                labelAlign="center"
                className="col-md-2"
                onClick={setSorting}
              />
            ) : null}
            <SortableTh
              field="number"
              name="invoicesSorting"
              label="Number"
              sorting={sorting}
              innerClassName="text-left display-flex"
              className={`col-md-${showOrgColumn ? '1' : '2'}`}
              onClick={setSorting}
            />
            <SortableTh
              field="event"
              name="invoicesSorting"
              label="Event Date"
              sorting={sorting}
              innerClassName="text-left display-flex"
              className={`col-md-${showOrgColumn ? '2' : '3'}`}
              onClick={setSorting}
            />
            <SortableTh
              field="total"
              name="invoicesSorting"
              label="Total"
              sorting={sorting}
              innerClassName="text-right display-flex"
              labelAlign="right"
              className="col-md-1"
              onClick={setSorting}
            />
            <SortableTh
              field="paidAmount"
              name="invoicesSorting"
              label="Amount Paid"
              sorting={sorting}
              innerClassName="text-right display-flex"
              className="col-md-1"
              onClick={setSorting}
            />
            <SortableTh
              field="balance"
              name="invoicesSorting"
              label="Balance"
              sorting={sorting}
              innerClassName="text-right display-flex"
              labelAlign="right"
              className="col-md-1"
              onClick={setSorting}
            />
            <th className="text-center col-md-2">Status/Actions</th>
          </tr>
          </thead>
          <tbody>
          {list.map(item => {
            const isInvoice = item.type === 'invoice';
            const opertation = isInvoice ? invoices[item.id] : transactions[item.id];
            if (!opertation) return null;
            const event =
              opertation.eventId && events[opertation.eventId]
                ? events[opertation.eventId]
                : null;
            const org = organizations[opertation.organizationId];
            const type = isInvoice ? 'Invoice' : opertation.type;
            const number = isInvoice
              ? `Invoice #${opertation.number}`
              : opertation.invoiceId && invoices[opertation.invoiceId]
                ? `For Invoice #${invoices[opertation.invoiceId].number}`
                : null;
            const date = isInvoice ? opertation.userCreatedDateTime : opertation.createdDateTime;
            const totalPrice = isInvoice ? opertation.totalPrice : opertation.amount;

            const isStripePayment = opertation.type === 'Stripe Payment';
            if (!org) return null;

            return (
              <tr
                key={`${item.type}-${item.id}`}
                className="cursor-pointer"
                data-invoice-id={item.id}
                data-type={item.type}
                data-org-unique={org.unique}
                onClick={onInvoiceClick}
                title="View invoice"
              >
                {/*Date*/}
                <td className="text-left col-md-1 td-custom-left">{format(date, 'd M Y')}</td>
                {/*Type*/}
                <td className="text-left col-md-1 td-custom-left">{type}</td>
                {/*Organization*/}
                {showOrgColumn ? (
                  <td className="text-left col-md-2 td-custom-left">{org.name}</td>
                ) : null}
                {/*Number*/}
                <td
                  className={`text-left text-bold td-custom-left col-md-${
                    showOrgColumn ? '1' : '2'
                  }`}
                >
                  {number ? (
                    <a
                      target="_blank"
                      href={Invoice.getInvoiceLink(
                        isInvoice ? opertation.id : opertation.invoiceId,
                        org.unique
                      )}
                    >
                      {number}
                    </a>
                  ) : (
                    <span>—</span>
                  )}
                </td>
                {/*Event Time*/}
                <td className={`text-left td-custom-left col-md-${showOrgColumn ? '2' : '3'}`}>
                  {event && event.id ? (
                    calendarView ? (
                      <a
                        target="_blank"
                        className="underline"
                        href={`${window.serverData._uri}/org/${org.unique}${event.link}`}
                      >
                        {event.period}
                      </a>
                    ) : (
                      <span>{event.period}</span>
                    )
                  ) : (
                    <span>—</span>
                  )}
                </td>
                {/*Total*/}
                <td className="text-right td-custom-right col-md-1">${totalPrice}</td>
                {/*Amount Paid*/}
                <td className="text-right td-custom-right col-md-1">
                  {opertation.paidAmount ? `$${opertation.paidAmount}` : <span>—</span>}
                </td>
                {/*Balance*/}
                <td className="text-right td-custom-right col-md-1">
                  {opertation.balance ? `$${opertation.balance}` : <span>—</span>}
                </td>
                {/*Status/Actions*/}
                <td className="text-center td-custom-center col-md-2">
                  <div className="action-btns-wrap">
                    {opertation.editable ? (
                      !Organization.orgAccess && opertation.directionType === 'outcome' ? (
                        <button
                          disabled={progress}
                          data-transaction-id={item.id}
                          onClick={onIncreaseClick}
                          className="btn btn-xs btn-success btn-action"
                        >
                          Increase
                        </button>
                      ) : (
                        <button
                          disabled={progress}
                          data-transaction-id={item.id}
                          onClick={onEditClick}
                          className="btn btn-xs btn-success btn-action"
                        >
                          Edit
                        </button>
                      )
                    ) : null}
                    {opertation.deletable ? (
                      <button
                        disabled={progress}
                        className={`btn btn-xs ${
                          isStripePayment ? 'btn-default' : 'btn-danger'
                        } btn-action`}
                        data-transaction-id={item.id}
                        onClick={deleteTransaction}
                      >
                        {isStripePayment ? 'Refund' : 'Delete'}
                      </button>
                    ) : null}
                    {opertation.refundable ? (
                      <button
                        disabled={progress}
                        data-transaction-id={item.id}
                        onClick={onRefundClick}
                        className="btn btn-xs btn-success btn-action"
                      >
                        Refund
                      </button>
                    ) : null}
                    {!isInvoice &&
                    !opertation.refundable &&
                    !opertation.editable &&
                    !opertation.deletable
                      ? '—'
                      : null}
                    {isInvoice && opertation.paidStatus === 'paid' ? (
                      <span
                        className={`invoice-paid-status invoice-paid-status-${opertation.paidStatus}`}
                      >
                          {opertation.paidStatus}
                        </span>
                    ) : null}
                    {isInvoice && opertation.paidStatus === 'unpaid' ? (
                      <a
                        className="btn btn-xs btn-success btn-action"
                        href={
                          URL.org
                            ? `${window.serverData._uri}/org/${org.unique}/invoices/pay/${opertation.payerId}/?=${opertation.id}`
                            : `/invoices/pay/${opertation.organizationId}/?=${opertation.id}`
                        }
                      >
                        Pay
                      </a>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
